<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  AdminShellLayout,
  type AdminLayoutMenuGroup,
  type AdminLayoutMenuItem,
  type AdminLayoutWorkspaceTab
} from '@xjlv2/admin-layout'
import type {
  ModuleMenuItem,
  ModuleMenuView,
  ShellNotification,
  PermissionManagementSection,
  WorkspaceTab,
  WorkspaceTabDefinition
} from '../../types'
import { isMarketCenterModule, isModuleNavigationRoot } from '../../utils/menuArchitecture'

interface PermissionSectionLink {
  key: PermissionManagementSection
  label: string
}

type ShellWorkspaceTab =
  | { id: string; label: string; group: string; kind: 'root'; tab: WorkspaceTab }
  | { id: string; label: string; group: string; kind: 'permission'; section: PermissionManagementSection }
  | { id: string; label: string; group: string; kind: 'module'; menu: ModuleMenuItem }

const props = defineProps<{
  activeTab: WorkspaceTab
  activePermissionSection: PermissionManagementSection
  activeMenuCode: string | null
  username: string
  modules: ModuleMenuView[]
  notifications: ShellNotification[]
  unreadCount: number
  permissionSections: PermissionSectionLink[]
  tabs: WorkspaceTabDefinition[]
  loading: boolean
}>()

const emit = defineEmits<{
  refresh: []
  logout: []
  'notification-open': [notification: ShellNotification]
  'notifications-mark-all-read': []
  'update:activeTab': [tab: WorkspaceTab]
  'open-menu': [menu: ModuleMenuItem]
  'open-permission-section': [section: PermissionManagementSection]
}>()

const legacyHiddenMenuCodes = new Set(['decoration.home'])
const visitedTabIds = ref<string[]>(['root:dashboard'])

const rootTabs = computed(() => props.tabs.filter((tab) => !['permissionManagement', 'notifications'].includes(tab.key)))
const notificationTabs = computed(() => props.tabs.filter((tab) => tab.key === 'notifications'))
const hasPermissionManagement = computed(
  () => props.tabs.some((tab) => tab.key === 'permissionManagement') && props.permissionSections.length > 0
)
const allWorkspaceTabs = computed<ShellWorkspaceTab[]>(() => [
  ...rootTabs.value.map((tab) => ({
    id: `root:${tab.key}`,
    label: tab.label,
    group: '系统',
    kind: 'root' as const,
    tab: tab.key
  })),
  ...notificationTabs.value.map((tab) => ({
    id: `root:${tab.key}`,
    label: tab.label,
    group: '站内通知',
    kind: 'root' as const,
    tab: tab.key
  })),
  ...props.permissionSections.map((section) => ({
    id: `permission:${section.key}`,
    label: section.label,
    group: '权限管理',
    kind: 'permission' as const,
    section: section.key
  })),
  ...props.modules.flatMap((module) =>
    module.visibleMenus
      .filter((menu) => !menu.hasChildren && !legacyHiddenMenuCodes.has(menu.code))
      .map((menu) => ({
        id: `module:${menu.code}`,
        label: menu.name,
        group: module.moduleName,
        kind: 'module' as const,
        menu
      }))
  )
])
const activeWorkspaceTabId = computed(() => {
  if (props.activeMenuCode) return `module:${props.activeMenuCode}`
  if (props.activeTab === 'permissionManagement') return `permission:${props.activePermissionSection}`
  return `root:${props.activeTab}`
})
const workspaceTabs = computed(() =>
  visitedTabIds.value
    .map((id) => allWorkspaceTabs.value.find((tab) => tab.id === id))
    .filter((tab): tab is ShellWorkspaceTab => tab !== undefined)
)
const activeWorkspaceTab = computed(() =>
  allWorkspaceTabs.value.find((tab) => tab.id === activeWorkspaceTabId.value) ?? workspaceTabs.value[0] ?? null
)
const breadcrumb = computed(() =>
  ['租户 SaaS', activeWorkspaceTab.value?.group, activeWorkspaceTab.value?.label].filter(Boolean).join(' / ')
)
const layoutMenuGroups = computed<AdminLayoutMenuGroup[]>(() => {
  const groups: AdminLayoutMenuGroup[] = [
    {
      id: 'system',
      label: '系统',
      icon: '系',
      subtitle: `${rootTabs.value.length} 个工作区`,
      items: rootTabs.value.map((tab) => ({
        id: `root:${tab.key}`,
        label: tab.label,
        icon: tab.label.slice(0, 1),
        active: !props.activeMenuCode && props.activeTab === tab.key
      }))
    }
  ]

  if (notificationTabs.value.length > 0) {
    groups.push({
      id: 'notifications',
      label: '站内通知',
      icon: '通',
      subtitle: props.unreadCount > 0 ? `${props.unreadCount} 条未读` : '消息中心',
      items: notificationTabs.value.map((tab) => ({
        id: `root:${tab.key}`,
        label: tab.label,
        icon: '通',
        badge: props.unreadCount > 0 ? props.unreadCount : undefined,
        active: !props.activeMenuCode && props.activeTab === tab.key
      }))
    })
  }

  if (hasPermissionManagement.value) {
    groups.push({
      id: 'permissionManagement',
      label: '权限管理',
      icon: '权',
      subtitle: `${props.permissionSections.length} 个管理项`,
      items: props.permissionSections.map((section) => ({
        id: `permission:${section.key}`,
        label: section.label,
        icon: section.label.slice(0, 1),
        active:
          !props.activeMenuCode &&
          props.activeTab === 'permissionManagement' &&
          props.activePermissionSection === section.key
      }))
    })
  }

  // 市场类模块归组进「市场中心」，其余模块保持独立分组；市场中心插到第一个市场模块原本的位置。
  const marketCenterItems: AdminLayoutMenuItem[] = []
  let marketCenterModuleCount = 0
  let marketCenterInsertIndex = -1

  for (const module of props.modules) {
    const sidebarMenus = module.visibleMenus.filter((menu) => !legacyHiddenMenuCodes.has(menu.code))
    const hiddenSidebarMenuCount = [...legacyHiddenMenuCodes]
      .filter((code) => code.startsWith(`${module.moduleCode}.`)).length
    const sidebarMenuTotal = Math.max(sidebarMenus.length, module.totalMenus - hiddenSidebarMenuCount)
    const moduleRoot = sidebarMenus.find(isModuleNavigationRoot)
    const sidebarItems = sidebarMenus.filter((menu) => menu.code !== moduleRoot?.code)
    const sidebarItemTotal = Math.max(sidebarItems.length, sidebarMenuTotal - (moduleRoot ? 1 : 0))

    if (isMarketCenterModule(module.moduleCode)) {
      marketCenterModuleCount += 1
      if (marketCenterInsertIndex < 0) marketCenterInsertIndex = groups.length
      marketCenterItems.push({
        id: `market-module:${module.moduleCode}`,
        label: module.moduleName,
        icon: module.moduleName.slice(0, 1),
        depth: 0,
        active: false,
        disabled: sidebarItems.length === 0
      })
      for (const menu of sidebarItems) {
        marketCenterItems.push({
          id: `module:${menu.code}`,
          label: menu.name,
          icon: menu.name.slice(0, 1),
          depth: 1 + Math.max(0, (menu.depth ?? 0) - (moduleRoot ? 1 : 0)),
          active: props.activeMenuCode === menu.code,
          disabled: menu.hasChildren
        })
      }
      continue
    }

    groups.push({
      id: `module:${module.moduleCode}`,
      label: module.moduleName,
      icon: module.moduleName.slice(0, 1),
      subtitle: `${sidebarItems.length}/${sidebarItemTotal} 菜单可见`,
      items: sidebarItems.map((menu) => ({
        id: `module:${menu.code}`,
        label: menu.name,
        icon: menu.name.slice(0, 1),
        depth: Math.max(0, (menu.depth ?? 0) - (moduleRoot ? 1 : 0)),
        active: props.activeMenuCode === menu.code,
        disabled: menu.hasChildren
      }))
    })
  }

  if (marketCenterItems.length > 0) {
    groups.splice(Math.max(0, marketCenterInsertIndex), 0, {
      id: 'market-center',
      label: '市场中心',
      icon: '市',
      subtitle: `${marketCenterModuleCount} 个市场`,
      items: marketCenterItems
    })
  }

  return groups
})
const layoutWorkspaceTabs = computed<AdminLayoutWorkspaceTab[]>(() =>
  workspaceTabs.value.map((tab) => ({
    id: tab.id,
    label: tab.label,
    closable: workspaceTabs.value.length > 1
  }))
)

function openPermissionSection(section: PermissionManagementSection) {
  rememberTab(`permission:${section}`)
  emit('open-permission-section', section)
}

function openModuleMenu(menu: ModuleMenuItem) {
  if (menu.hasChildren) return
  rememberTab(`module:${menu.code}`)
  emit('open-menu', menu)
}

function rememberTab(id: string) {
  if (!visitedTabIds.value.includes(id)) {
    visitedTabIds.value = [...visitedTabIds.value, id]
  }
}

function openWorkspaceTab(tab: ShellWorkspaceTab) {
  rememberTab(tab.id)
  if (tab.kind === 'root') {
    emit('update:activeTab', tab.tab)
    return
  }
  if (tab.kind === 'permission') {
    openPermissionSection(tab.section)
    return
  }
  openModuleMenu(tab.menu)
}

function openWorkspaceTabById(id: string) {
  const tab = allWorkspaceTabs.value.find((item) => item.id === id)
  if (tab) {
    openWorkspaceTab(tab)
  }
}

function openLayoutMenuItem(item: AdminLayoutMenuItem) {
  openWorkspaceTabById(item.id)
}

function openLayoutWorkspaceTab(tab: AdminLayoutWorkspaceTab) {
  openWorkspaceTabById(tab.id)
}

function openNotification(notification: ShellNotification) {
  emit('notification-open', notification)
}

function openNotificationCenter() {
  emit('update:activeTab', 'notifications')
}

function closeWorkspaceTab(tab: ShellWorkspaceTab) {
  if (workspaceTabs.value.length <= 1) return
  const currentIndex = visitedTabIds.value.indexOf(tab.id)
  const remaining = visitedTabIds.value.filter((id) => id !== tab.id)
  visitedTabIds.value = remaining
  if (tab.id !== activeWorkspaceTabId.value) return

  const nextId = remaining[Math.max(0, currentIndex - 1)] ?? remaining[0]
  const nextTab = allWorkspaceTabs.value.find((item) => item.id === nextId)
  if (nextTab) openWorkspaceTab(nextTab)
}

function closeLayoutWorkspaceTab(tab: AdminLayoutWorkspaceTab) {
  const workspaceTab = allWorkspaceTabs.value.find((item) => item.id === tab.id)
  if (workspaceTab) {
    closeWorkspaceTab(workspaceTab)
  }
}

function closeOtherWorkspaceTabs(tab?: AdminLayoutWorkspaceTab) {
  const targetId = tab?.id || activeWorkspaceTabId.value
  const affixIds = allWorkspaceTabs.value.filter((t) => t.kind === 'root').map((t) => t.id)
  visitedTabIds.value = [...new Set([...affixIds, targetId])]
  if (activeWorkspaceTabId.value !== targetId) {
    openWorkspaceTabById(targetId)
  }
}

function closeLeftWorkspaceTabs(tab: AdminLayoutWorkspaceTab) {
  const index = visitedTabIds.value.indexOf(tab.id)
  if (index <= 0) return
  const preserveIds = visitedTabIds.value.slice(index)
  const affixIds = allWorkspaceTabs.value.filter((t) => t.kind === 'root').map((t) => t.id)
  visitedTabIds.value = [...new Set([...affixIds, ...preserveIds])]
  if (!visitedTabIds.value.includes(activeWorkspaceTabId.value)) {
    openWorkspaceTabById(tab.id)
  }
}

function closeRightWorkspaceTabs(tab: AdminLayoutWorkspaceTab) {
  const index = visitedTabIds.value.indexOf(tab.id)
  if (index < 0) return
  const preserveIds = visitedTabIds.value.slice(0, index + 1)
  const affixIds = allWorkspaceTabs.value.filter((t) => t.kind === 'root').map((t) => t.id)
  visitedTabIds.value = [...new Set([...affixIds, ...preserveIds])]
  if (!visitedTabIds.value.includes(activeWorkspaceTabId.value)) {
    openWorkspaceTabById(tab.id)
  }
}

function closeAllWorkspaceTabs() {
  const firstTab = allWorkspaceTabs.value[0]
  if (!firstTab) return
  visitedTabIds.value = [firstTab.id]
  openWorkspaceTab(firstTab)
}

function handleTabRefresh(tab: AdminLayoutWorkspaceTab) {
  if (tab.id !== activeWorkspaceTabId.value) {
    openWorkspaceTabById(tab.id)
  }
  emit('refresh')
}

watch(activeWorkspaceTabId, (id) => {
  if (allWorkspaceTabs.value.some((tab) => tab.id === id)) {
    rememberTab(id)
  }
})

watch(allWorkspaceTabs, (tabs) => {
  visitedTabIds.value = visitedTabIds.value.filter((id) => tabs.some((tab) => tab.id === id))
  if (visitedTabIds.value.length === 0 && tabs[0]) {
    visitedTabIds.value = [tabs[0].id]
  }
})
</script>

<template>
  <AdminShellLayout
    brand-title="租户 SaaS"
    brand-mark="S"
    :breadcrumb="breadcrumb"
    :title="activeWorkspaceTab?.label ?? 'SaaS 工作台'"
    :username="username"
    :loading="loading"
    :menu-groups="layoutMenuGroups"
    :workspace-tabs="layoutWorkspaceTabs"
    :active-workspace-tab-id="activeWorkspaceTabId"
    @refresh="emit('refresh')"
    @logout="emit('logout')"
    @menu-item-click="openLayoutMenuItem"
    @workspace-tab-click="openLayoutWorkspaceTab"
    @workspace-tab-close="closeLayoutWorkspaceTab"
    @workspace-tab-refresh="handleTabRefresh"
    @workspace-close-other="closeOtherWorkspaceTabs"
    @workspace-close-left="closeLeftWorkspaceTabs"
    @workspace-close-right="closeRightWorkspaceTabs"
    @workspace-close-all="closeAllWorkspaceTabs"
  >
    <template #header-actions>
      <slot
        name="notifications"
        :notifications="notifications"
        :unread-count="unreadCount"
        :open="openNotification"
        :mark-all-read="() => emit('notifications-mark-all-read')"
        :open-center="openNotificationCenter"
      />
      <slot name="agent" />
    </template>
    <slot />
  </AdminShellLayout>
</template>
